import { EventEmitter } from '../../core/Event';
import type { HoldAndSpinEvent, MoneyCell } from './HoldAndSpinEvents';

export interface HoldAndSpinModelConfig {
  /** Number of cols × rows on the slot grid — used to detect "full grid"
   * for the GRAND-jackpot fanfare. cs-core's MMS uses 5×3 = 15. */
  gridCols: number;
  gridRows: number;
  /** Respins remaining when the bonus starts AND when new cells land — the
   * "every new symbol resets you to N" rule that makes the bonus exciting.
   * MMS-classic uses 3. */
  initialRespins: number;
}

/**
 * Authoritative state for an active hold-and-spin bonus run. Owns:
 *
 * <ul>
 *   <li>The set of locked money cells (one per (col, row), keyed for O(1)
 *       lookup so the machine layer can ask "is this cell locked").
 *   <li>The respin counter (decrements on no-land, resets to {@code initialRespins}
 *       on land).
 *   <li>Phase enum — {@code idle} → {@code active} → {@code finished}.
 * </ul>
 *
 * <p>Mutation is via methods that emit events; reads are via getters. Keeps
 * the bonus testable in isolation from the Pixi machine + the slot scene.
 *
 * <p>Lifted-in-spirit from cs-core's MMS hold-and-spin sub-game; the
 * v0 surface is the bare minimum to drive a 6+ trigger / 3-respin /
 * reset-on-land / sum-payout bonus. Mini/Minor/Major/Grand pots,
 * collect-symbol multipliers, and progressive jackpots land later.
 */
export class HoldAndSpinModel {
  public readonly events = new EventEmitter<HoldAndSpinEvent>();

  private readonly config: HoldAndSpinModelConfig;
  private _phase: 'idle' | 'active' | 'finished' = 'idle';
  private _lockedCells = new Map<string, MoneyCell>();
  private _respinsRemaining = 0;
  /** 0 = trigger spin, 1 = first respin, … — used to tag {@code MoneyCell.roundLanded}. */
  private _round = 0;

  constructor(config: HoldAndSpinModelConfig) {
    this.config = config;
  }

  // -------- queries --------

  get phase(): 'idle' | 'active' | 'finished' {
    return this._phase;
  }

  get respinsRemaining(): number {
    return this._respinsRemaining;
  }

  get lockedCells(): ReadonlyMap<string, MoneyCell> {
    return this._lockedCells;
  }

  get totalLocked(): number {
    return this._lockedCells.size;
  }

  get round(): number {
    return this._round;
  }

  /** Sum of every locked cell's value — the bonus payout. */
  totalWinnings(): number {
    let sum = 0;
    for (const cell of this._lockedCells.values()) sum += cell.value;
    return sum;
  }

  /** All grid positions filled with money symbols — triggers GRAND fanfare
   * and short-circuits the respin loop. */
  isGridFull(): boolean {
    return this._lockedCells.size >= this.config.gridCols * this.config.gridRows;
  }

  hasCellLocked(col: number, row: number): boolean {
    return this._lockedCells.has(cellKey(col, row));
  }

  /** Snapshot of every locked cell — useful for UI re-renders that don't
   * want to iterate the live map. */
  cellsList(): readonly MoneyCell[] {
    return [...this._lockedCells.values()];
  }

  // -------- mutations --------

  /**
   * Begin the bonus. {@code initialCells} are the money symbols that
   * triggered (counted ≥ trigger threshold by the caller — the model
   * doesn't enforce the threshold so tests can drive small grids).
   *
   * <p>Throws if already active or if any two cells share a position.
   */
  start(initialCells: readonly Omit<MoneyCell, 'roundLanded'>[], bet: number): void {
    if (this._phase !== 'idle') {
      throw new Error(`HoldAndSpinModel.start: phase is ${this._phase}, expected idle`);
    }
    this._phase = 'active';
    this._round = 0;
    for (const cell of initialCells) {
      const key = cellKey(cell.col, cell.row);
      if (this._lockedCells.has(key)) {
        throw new Error(`HoldAndSpinModel.start: duplicate cell at ${key}`);
      }
      this._lockedCells.set(key, { ...cell, roundLanded: 0 });
    }
    this._respinsRemaining = this.config.initialRespins;
    this.events.emit({
      kind: 'hold-and-spin-started',
      cells: this.cellsList(),
      initialRespins: this.config.initialRespins,
      bet,
    });
  }

  /**
   * One respin just landed; record any new money cells. Returns the count
   * of newly-locked cells. {@code newCells} should NOT include cells already
   * locked from prior rounds — the caller filters those out.
   *
   * <p>Side effect: resets {@code respinsRemaining} to {@code initialRespins}
   * (the "every new symbol resets you" rule). If no new cells, the caller
   * should call {@link consumeRespin} instead.
   */
  recordLandedCells(newCells: readonly Omit<MoneyCell, 'roundLanded'>[]): number {
    if (this._phase !== 'active') {
      throw new Error(`HoldAndSpinModel.recordLandedCells: phase is ${this._phase}, expected active`);
    }
    if (newCells.length === 0) return 0;
    this._round += 1;
    const stamped: MoneyCell[] = [];
    for (const cell of newCells) {
      const key = cellKey(cell.col, cell.row);
      if (this._lockedCells.has(key)) {
        // Defensive — shouldn't happen if caller filters, but skip duplicates
        // rather than throw. The roundLanded of the existing cell wins.
        continue;
      }
      const stampedCell: MoneyCell = { ...cell, roundLanded: this._round };
      this._lockedCells.set(key, stampedCell);
      stamped.push(stampedCell);
    }
    if (stamped.length === 0) return 0;
    this.events.emit({
      kind: 'hold-and-spin-cells-landed',
      newCells: stamped,
      totalLocked: this._lockedCells.size,
    });
    const before = this._respinsRemaining;
    this._respinsRemaining = this.config.initialRespins;
    if (before !== this._respinsRemaining) {
      this.events.emit({
        kind: 'hold-and-spin-respins-changed',
        before,
        after: this._respinsRemaining,
      });
    }
    return stamped.length;
  }

  /**
   * One respin just landed with NO new money cells; decrement the counter.
   * The caller invokes this OR {@link recordLandedCells}, never both for
   * the same respin. Increments {@link round} so a "wasted respin" still
   * marks progress.
   */
  consumeRespin(): void {
    if (this._phase !== 'active') {
      throw new Error(`HoldAndSpinModel.consumeRespin: phase is ${this._phase}, expected active`);
    }
    if (this._respinsRemaining <= 0) return;
    this._round += 1;
    const before = this._respinsRemaining;
    this._respinsRemaining -= 1;
    this.events.emit({
      kind: 'hold-and-spin-respins-changed',
      before,
      after: this._respinsRemaining,
    });
  }

  /**
   * Mark the bonus finished and emit the completion event. Caller invokes
   * once the respin loop exits — either {@code respinsRemaining === 0} or
   * {@code isGridFull()}. After this, the model is single-shot — a fresh
   * model instance handles the next bonus.
   */
  complete(): void {
    if (this._phase !== 'active') return;
    this._phase = 'finished';
    this.events.emit({
      kind: 'hold-and-spin-completed',
      totalWinnings: this.totalWinnings(),
      cells: this.cellsList(),
      fullGrid: this.isGridFull(),
    });
  }

  /** Drop every event listener. Call when the host scene destroys. */
  dispose(): void {
    this.events.clear();
  }
}

/** Stable map key for (col, row). */
export function cellKey(col: number, row: number): string {
  return `${col}:${row}`;
}
